import { AlertTriangle, Check } from 'lucide-react-native';
import { useCallback, useMemo, useState } from 'react';
import { ScrollView, StyleSheet } from 'react-native';
import { HStack, Text, VStack } from '../../../components/primitives';
import { Button } from '../../../components/primitives/Button';
import { Sheet } from '../../../components/primitives/Sheet';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import type { ExtractedContact, WebSearchResult } from '../types';
import { ContactResultCard } from './ContactResultCard';

interface WebSearchResultsSheetProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: (contacts: ExtractedContact[], force?: boolean) => void;
  onRetry: () => void;
  result: WebSearchResult | null;
  isConfirming?: boolean;
  existingEmail?: string;
  existingPhone?: string;
}

export function WebSearchResultsSheet({
  visible,
  onClose,
  onConfirm,
  onRetry,
  result,
  isConfirming,
  existingEmail,
  existingPhone,
}: WebSearchResultsSheetProps) {
  const { t } = useTranslation();
  const [selectedEmail, setSelectedEmail] = useState<string | null>(null);
  const [selectedPhone, setSelectedPhone] = useState<string | null>(null);

  const contacts = result?.contacts ?? [];
  const hasResults = contacts.length > 0;

  const emailContacts = useMemo(() => contacts.filter((c) => c.type === 'email'), [contacts]);
  const phoneContacts = useMemo(() => contacts.filter((c) => c.type === 'phone'), [contacts]);

  const selectContact = (contact: ExtractedContact) => {
    const key = `${contact.type}:${contact.value}`;
    if (contact.type === 'email') {
      setSelectedEmail((prev) => (prev === key ? null : key));
    } else {
      setSelectedPhone((prev) => (prev === key ? null : key));
    }
  };

  const isSelected = (contact: ExtractedContact) => {
    const key = `${contact.type}:${contact.value}`;
    return contact.type === 'email' ? selectedEmail === key : selectedPhone === key;
  };

  const selectedContacts = useMemo(
    () =>
      contacts.filter((c) => {
        const key = `${c.type}:${c.value}`;
        return key === selectedEmail || key === selectedPhone;
      }),
    [contacts, selectedEmail, selectedPhone],
  );

  const willOverwriteEmail =
    !!existingEmail &&
    selectedContacts.some((c) => c.type === 'email' && c.value !== existingEmail);
  const willOverwritePhone =
    !!existingPhone &&
    selectedContacts.some((c) => c.type === 'phone' && c.value !== existingPhone);
  const willOverwrite = willOverwriteEmail || willOverwritePhone;

  const [showOverwriteConfirm, setShowOverwriteConfirm] = useState(false);

  const overwriteWarningParts: string[] = [];
  if (willOverwriteEmail) overwriteWarningParts.push(t('leads.webSearch.emailLabel'));
  if (willOverwritePhone) overwriteWarningParts.push(t('leads.webSearch.phoneLabel'));

  const handleConfirmPress = useCallback(() => {
    if (willOverwrite) {
      setShowOverwriteConfirm(true);
    } else {
      onConfirm(selectedContacts);
    }
  }, [willOverwrite, selectedContacts, onConfirm]);

  return (
    <Sheet
      visible={visible}
      onClose={onClose}
      position="center"
      size="md"
      title={t('leads.webSearch.resultsTitle')}
    >
      <VStack spacing="md">
        {showOverwriteConfirm ? (
          <VStack spacing="md" style={{ paddingVertical: 8 }}>
            <HStack spacing="sm" style={{ alignItems: 'center' }}>
              <AlertTriangle size={20} color={colors.status.warning.main} />
              <Text variant="body" weight="semibold" color={colors.status.warning.main}>
                {t('leads.webSearch.overwriteTitle')}
              </Text>
            </HStack>
            <Text variant="bodySmall" color={colors.text.secondary}>
              {t('leads.webSearch.overwriteDescription', {
                fields: overwriteWarningParts.join(` ${t('common.and')} `),
              })}
            </Text>
            <HStack spacing="sm" style={{ justifyContent: 'flex-end' }}>
              <Button variant="ghost" size="sm" onPress={() => setShowOverwriteConfirm(false)}>
                {t('common.actions.cancel')}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onPress={() => {
                  setShowOverwriteConfirm(false);
                  onConfirm(selectedContacts, true);
                }}
                loading={isConfirming}
              >
                {t('leads.webSearch.overwriteConfirm')}
              </Button>
            </HStack>
          </VStack>
        ) : hasResults ? (
          <>
            <Text variant="bodySmall" color={colors.text.secondary}>
              {t('leads.webSearch.foundSummary', { count: contacts.length })}
            </Text>

            <ScrollView style={styles.resultsList} nestedScrollEnabled>
              <VStack spacing="md">
                {emailContacts.length > 0 && (
                  <VStack spacing="md">
                    <Text variant="bodySmall" weight="semibold" color={colors.text.secondary}>
                      {t('leads.webSearch.emailsFound')}
                    </Text>
                    {emailContacts.map((contact, idx) => (
                      <ContactResultCard
                        key={`${contact.type}:${contact.value}:${idx}`}
                        contact={contact}
                        selected={isSelected(contact)}
                        onToggle={() => selectContact(contact)}
                      />
                    ))}
                  </VStack>
                )}
                {phoneContacts.length > 0 && (
                  <VStack spacing="md">
                    <Text variant="bodySmall" weight="semibold" color={colors.text.secondary}>
                      {t('leads.webSearch.phonesFound')}
                    </Text>
                    {phoneContacts.map((contact, idx) => (
                      <ContactResultCard
                        key={`${contact.type}:${contact.value}:${idx}`}
                        contact={contact}
                        selected={isSelected(contact)}
                        onToggle={() => selectContact(contact)}
                      />
                    ))}
                  </VStack>
                )}
              </VStack>
            </ScrollView>

            <Text variant="caption" color={colors.text.tertiary} align="center">
              {t('leads.webSearch.selectedCount', { count: selectedContacts.length })}
            </Text>

            <HStack spacing="sm" style={{ justifyContent: 'flex-end' }}>
              <Button variant="ghost" size="sm" onPress={onClose}>
                {t('leads.webSearch.discard')}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onPress={handleConfirmPress}
                disabled={selectedContacts.length === 0}
                loading={isConfirming}
              >
                <Check size={14} color={colors.foreground} style={{ marginRight: 4 }} />
                {t('leads.webSearch.confirmSave')}
              </Button>
            </HStack>
          </>
        ) : (
          <VStack spacing="md" style={{ alignItems: 'center', paddingVertical: 12 }}>
            <Text variant="body" color={colors.text.secondary} align="center">
              {t('leads.webSearch.noResults')}
            </Text>
            <Text variant="bodySmall" color={colors.text.tertiary} align="center">
              {t('leads.webSearch.noResultsDescription')}
            </Text>

            <HStack spacing="sm">
              <Button variant="ghost" size="sm" onPress={onClose}>
                {t('common.actions.close')}
              </Button>
              <Button variant="outline" size="sm" onPress={onRetry}>
                {t('leads.webSearch.tryAgain')}
              </Button>
            </HStack>
          </VStack>
        )}
      </VStack>
    </Sheet>
  );
}

const styles = StyleSheet.create({
  resultsList: {
    maxHeight: 400,
  },
});
